import type { LexData, MembershipCategory } from "./types";
import { realParticipationPoints } from "./governanceDemo";
import {
  calculateMembershipParticipationBonus,
  calculateVotingEntitlement,
  defaultMembershipVotingProfile,
  type DecisionType,
  type ParticipationCategory,
  type ParticipationPointEntry,
} from "./voting";

const PERIOD = "2026-H1";

export interface ContributionBreakdownItem {
  category: ParticipationCategory | "discussions_started" | "comments" | "amendments" | "votes" | "legislative";
  label: string;
  count: number;
}

export interface FirmContribution {
  firmId: string;
  firmName: string;
  jurisdiction: string;
  membershipCategory: MembershipCategory;
  discussionsStarted: number;
  comments: number;
  amendmentsSponsored: number;
  votesCast: number;
  legislativeContributions: number;
  validatedPoints: number;
  baseWeight: number;
  participationBonus: number;
  totalWeight: number;
  maxTotalWeight: number;
  capUsedPct: number;
  bindingVote: boolean;
  breakdown: ContributionBreakdownItem[];
}

const categoryLabels: Record<ParticipationCategory, string> = {
  meeting_attendance: "Meetings attended",
  proposal_submitted: "Proposals submitted",
  proposal_comment: "Proposal comments",
  amendment_submitted: "Amendments submitted",
  working_group_contribution: "Working-group work",
  document_drafting: "Document drafting",
  eu_affairs_contribution: "EU affairs work",
  vote_cast: "Votes cast",
  administrative_support: "Administrative support",
  peer_recognition: "Peer recognition",
};

export function buildFirmContribution(data: LexData, firmId: string, decisionType: DecisionType = "ordinary"): FirmContribution | null {
  const firm = data.firms.find((item) => item.id === firmId);
  if (!firm) return null;
  const participation = realParticipationPoints(data);

  const discussionsStarted = data.discussions.filter((item) => item.createdByFirmId === firmId).length;
  const comments = data.contributions.filter((item) => item.createdByFirmId === firmId).length;
  const amendmentsSponsored = data.amendments.filter((item) => item.sponsorFirmId === firmId).length;
  const votesCast = new Set(data.votes.filter((item) => item.firmId === firmId).map((item) => item.proposalId)).size;
  const legislativeContributions = data.lobbyingProjects.filter((item) => item.ownerFirmId === firmId).length;

  const validatedPoints = participation
    .filter((entry) => entry.firmId === firmId && entry.period === PERIOD && entry.validationStatus === "validated")
    .reduce((sum, entry) => sum + Math.max(0, entry.points), 0);

  const participationBonus = calculateMembershipParticipationBonus(participation, firmId, PERIOD, firm.membershipCategory);
  const entitlement = calculateVotingEntitlement({
    memberEntityId: firmId,
    membershipCategory: firm.membershipCategory,
    period: PERIOD,
    participationBonus,
    decisionType,
  });
  const profile = defaultMembershipVotingProfile(firm.membershipCategory);

  const pointsByCategory = new Map<ParticipationCategory, number>();
  for (const entry of participation.filter((item) => item.firmId === firmId && item.period === PERIOD)) {
    pointsByCategory.set(entry.category, (pointsByCategory.get(entry.category) ?? 0) + entry.points);
  }

  const breakdown: ContributionBreakdownItem[] = [];
  breakdown.push({ category: "discussions_started", label: "Discussions & notes started", count: discussionsStarted });
  breakdown.push({ category: "comments", label: "Contributions & comments", count: comments });
  breakdown.push({ category: "amendments", label: "Amendments sponsored", count: amendmentsSponsored });
  breakdown.push({ category: "votes", label: "Decisions voted", count: votesCast });
  breakdown.push({ category: "legislative", label: "Legislative projects led", count: legislativeContributions });
  const surfacedCategories: ParticipationCategory[] = ["document_drafting", "working_group_contribution", "eu_affairs_contribution", "meeting_attendance"];
  for (const [category, count] of pointsByCategory) {
    if (surfacedCategories.includes(category)) breakdown.push({ category, label: categoryLabels[category], count });
  }
  const filteredBreakdown = breakdown.filter((item) => item.count > 0);

  return {
    firmId,
    firmName: firm.name,
    jurisdiction: firm.jurisdiction,
    membershipCategory: firm.membershipCategory,
    discussionsStarted,
    comments,
    amendmentsSponsored,
    votesCast,
    legislativeContributions,
    validatedPoints,
    baseWeight: entitlement.baseWeight,
    participationBonus: entitlement.participationBonus,
    totalWeight: entitlement.totalWeight,
    maxTotalWeight: entitlement.maxTotalWeight,
    capUsedPct: profile.maxParticipationBonus > 0 ? Math.min(1, entitlement.participationBonus / profile.maxParticipationBonus) : 0,
    bindingVote: entitlement.baseWeight > 0,
    breakdown: filteredBreakdown,
  };
}

export function buildParticipationLeaderboard(data: LexData, decisionType: DecisionType = "ordinary"): FirmContribution[] {
  return data.firms
    .filter((firm) => firm.status === "active" && firm.membershipCategory !== "Observer")
    .map((firm) => buildFirmContribution(data, firm.id, decisionType))
    .filter((item): item is FirmContribution => item !== null)
    .sort((a, b) => b.validatedPoints - a.validatedPoints || b.totalWeight - a.totalWeight);
}

export function participationPointsFor(data: LexData): ParticipationPointEntry[] {
  return realParticipationPoints(data);
}
